/**
 * @file StaffNoContent.tsx
 * @brief The Super Admin "metadata only" line and the calm private state.
 *
 * @details
 * VERBLIZR staff never read user content (DES 22-10-2026, B2/W4). Super Admin
 * meeting and session pages carry one line saying so, and a content route
 * that refuses staff (403 `STAFF_NO_CONTENT`) shows a quiet "Private to the
 * account owner" state instead of an error.
 *
 * @author DES
 * @date 22-10-2026
 */

import { Lock } from "lucide-react";
import { STAFF_NO_CONTENT_LINE } from "@/shared/api/staffNoContent";
import { cn } from "@/shared/theme/cn";

/** The one line on every Super Admin page that lists meetings or sessions. */
export function StaffNoContentNote({ className }: { className?: string }) {
  return (
    <p className={cn("inline-flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
      <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {STAFF_NO_CONTENT_LINE}
    </p>
  );
}

/** Where content would be: what exists, never what it says. */
export function PrivateToOwnerState({
  detail,
  withLine = true,
  className,
}: {
  /** e.g. "42 transcript lines · audio kept" */
  detail?: string | null;
  /** False where the page already shows `StaffNoContentNote`. */
  withLine?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 rounded-[20px] bg-app-card px-6 py-8 text-center",
        className,
      )}
    >
      <Lock className="h-8 w-8 text-app-text-secondary" strokeWidth={1.5} aria-hidden />
      <p className="text-[15px] font-semibold text-app-text">Private to the account owner</p>
      {detail ? <p className="text-[13px] text-app-text-secondary">{detail}</p> : null}
      {withLine ? (
        <p className="text-[13px] text-app-text-secondary">{STAFF_NO_CONTENT_LINE}</p>
      ) : null}
    </div>
  );
}
